class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.handleClick.bind(this));
  }
  async handleClick(event) {
    event.preventDefault();
    const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');

    cartItems.updateQuantity(this.dataset.index, 0);
    await this.triggerCartempty();
  }

  async triggerCartempty() {
    setTimeout(async() => {
      await this.updateCartData();
    }, 800);
    document.addEventListener('cart:empty', () => {
      this.rerenderFeaturedCollection()
    });
  }

  async getCartData() {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Error fetching cart data: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch cart data:', error);
      return null;
    }
  }

  async updateCartData() {
    const cartData = await this.getCartData();

    if (!cartData) {
      console.error('Cart data could not be fetched.');
    }

    if (cartData && cartData.item_count === 0) {
      const event = new CustomEvent('cart:empty', {
        detail: cartData.item_count,
      });

      document.dispatchEvent(event);
    }

    return cartData;
  }

  async rerenderFeaturedCollection() {
    try {
      const response = await fetch('/cart', {
        method: 'GET',
        headers: {
          'Content-Type': 'text/html',
          Accept: 'text/html',
        },
      });

      if (!response.ok) {
        throw new Error(`Error fetching cart HTML: ${response.statusText}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newElement = doc.querySelector('.cart-drawer__collection');

      if (newElement) {
        document.querySelector('.cart-drawer__collection').innerHTML = newElement.innerHTML;
      }
    }
    catch (error) {
      console.error('Error updating cart sections:', error);
    }
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }
  onCartUpdate() {
    const isCartDrawer = this.tagName === 'CART-DRAWER-ITEMS';
    const sectionId = isCartDrawer ? 'cart-drawer' : 'main-cart-items';

    // Fetch updated cart content
    fetch(`${routes.cart_url}?section_id=${sectionId}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        if (isCartDrawer) {
          // Update cart drawer
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          this.updateElements(html, selectors);

          // Update main cart if we're on the cart page
          if (window.location.pathname.includes('/cart')) {
            this.updateMainCartIfVisible();
          }
        } else {
          // Update main cart
          const sourceQty = html.querySelector('cart-items');
          if (sourceQty) {
            this.innerHTML = sourceQty.innerHTML;
          }

          // Update cart drawer if it's open
          this.updateCartDrawerIfOpen();
        }
      })
      .catch(error => {
        console.error('Error updating cart:', error);
      });
  }

  // Helper to update multiple elements at once
  updateElements(html, selectors) {
    for (const selector of selectors) {
      const targetElement = document.querySelector(selector);
      const sourceElement = html.querySelector(selector);
      if (targetElement && sourceElement) {
        targetElement.replaceWith(sourceElement);
      }
    }
  }

  // Helper method to update main cart page if it's visible
  updateMainCartIfVisible() {
    // Only proceed if we're on the cart page
    if (!window.location.pathname.includes('/cart')) return;

    // Define elements to update and selectors to match them
    const cartElements = {
      'main-cart-items': {
        element: document.getElementById('main-cart-items'),
        source: 'cart-items',
        target: 'cart-items'
      },
      'main-cart-footer': {
        element: document.getElementById('main-cart-footer'),
        source: '#main-cart-footer .js-contents',
        target: '.js-contents'
      },
      'cart-page-item-count': {
        element: document.getElementById('cart-page-item-count'),
        source: '.cart-page-item-count',
        target: null // Use the element itself
      }
    };

    // Don't proceed if no cart elements exist
    if (!Object.values(cartElements).some(item => item.element)) return;

    // Fetch and update
    fetch(`${routes.cart_url}?section_id=main-cart-items`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        // Update each element if it exists
        for (const config of Object.values(cartElements)) {
          if (!config.element) continue;

          const sourceElement = html.querySelector(config.source);
          if (!sourceElement) continue;

          if (config.target) {
            // For elements where we update a child
            const targetElement = config.element.querySelector(config.target);
            if (targetElement) {
              targetElement.innerHTML = sourceElement.innerHTML;
            }
          } else {
            // For elements we update directly
            config.element.innerHTML = sourceElement.innerHTML;
          }
        }
      })
      .catch(error => {
        console.error('Error updating main cart page:', error);
      });
  }

  // Helper method to update cart drawer if it's open
  updateCartDrawerIfOpen() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || !cartDrawer.classList.contains('active')) return;

    fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then(response => response.text())
      .then(responseText => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        this.updateElements(html, ['cart-drawer-items', '.cart-drawer__footer']);
      })
      .catch(error => {
        console.error('Error updating cart drawer:', error);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
      {
        id: "cart-page-item-count",
        section: document.getElementById("cart-page-item-count").dataset.id,
        selector: ".cart-page-item-count",
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    return new Promise((resolve, reject) => {
      fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
        .then((response) => {
          return response.text();
        })
        .then(async(state) => {
          const parsedState = JSON.parse(state);
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });
          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);

          const lineItem =
            document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }

          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
        })
        .catch(() => {
          this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
          const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
          errors.textContent = window.cartStrings.error;
          reject()
        })
        .finally(() => {
          this.disableLoading(line);
          resolve()
        });
    })
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);
    const elements = [...cartItemElements, ...cartDrawerItemElements];

    elements.forEach((element) => element.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);
    const elements = [...cartItemElements, ...cartDrawerItemElements];

    elements.forEach((element) => element.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}